/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import SectionSidebar from '../components/SectionSidebar';
import {
  Container, Grid, Typography, Button, Box, CircularProgress, Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CelebrationIcon from '@mui/icons-material/Celebration';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const GOLD = '#d4af37';

interface DonatePageContent {
  title: string;
  subtitle: string;
  mainDescription: string;
  suggestedLevelsTitle: string;
  level1: { title: string; amount: string; description: string };
  level2: { title: string; amount: string; description: string };
  level3: { title: string; amount: string; description: string };
  usageTitle: string;
  usageDescription: string;
  onlineDonationTitle: string;
  onlineDonationDescription: string;
  bankTransferTitle: string;
  bankTransferDescription: string;
  impactTitle: string;
  impactDescription: string;
}

export default function Donate() {
  const [content, setContent] = useState<DonatePageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [donationEnabled, setDonationEnabled] = useState(true);

  useEffect(() => {
    const docRef = doc(db, 'donate_page_content', 'main');
    let isSubscribed = true;
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (!isSubscribed) return;
      try {
        if (docSnap.exists()) {
          setContent(docSnap.data() as DonatePageContent);
        } else {
          setContent(null);
        }
      } catch (error) {
        console.error('Error processing donate content:', error);
      } finally {
        setLoading(false);
      }
    }, () => { setLoading(false); });
    return () => { isSubscribed = false; unsubscribe(); };
  }, []);

  useEffect(() => {
    const docRef = doc(db, 'online_donation_content', 'main');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setDonationEnabled(data.enabled !== false);
      }
    });
    return () => unsubscribe();
  }, []);

  const defaultContent: DonatePageContent = {
    title: 'Support the Temple',
    subtitle: 'Your contributions sustain our sacred traditions and community service',
    mainDescription: 'Your donations sustain daily rituals, annadanam (community meals), temple maintenance, and festival arrangements. We offer multiple ways to contribute — one-time gifts, recurring donations, project-specific funding, and in-kind support.',
    suggestedLevelsTitle: 'Suggested Giving Levels',
    level1: { title: 'Annadanam Pack', amount: 'Rs 500', description: 'Provides meals for a family of four.' },
    level2: { title: 'Renovation Contribution', amount: 'Rs 5,000', description: 'Supports minor repairs and materials.' },
    level3: { title: 'Festival Sponsor', amount: 'Rs 20,000', description: 'Helps underwrite festival costs and arrangements.' },
    usageTitle: 'How Donations Are Used',
    usageDescription: 'We maintain transparent accounting and allocate funds to ceremonies, community services, structural upkeep, priest support, and festival activities.',
    onlineDonationTitle: 'One-time Online Donation',
    onlineDonationDescription: 'Use our secure online gateway to make a single donation. Please include your name and purpose in the form.',
    bankTransferTitle: 'Bank Transfer & Collections',
    bankTransferDescription: 'For larger gifts or recurring support, contact the temple office for bank details and receipts.',
    impactTitle: 'Impact Stories',
    impactDescription: 'Brief examples of how donor support has enabled free meals, repaired temple structures, and funded festival lighting and cultural programs.',
  };

  const d = content || defaultContent;

  const levels = [
    { ...d.level1, gradient: 'linear-gradient(135deg, #059669, #047857)', Icon: RestaurantIcon },
    { ...d.level2, gradient: 'linear-gradient(135deg, #d97706, #b45309)', Icon: AccountBalanceIcon },
    { ...d.level3, gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)', Icon: CelebrationIcon },
  ];

  const methods = [
    { title: d.onlineDonationTitle, desc: d.onlineDonationDescription, to: '/donate/online', btn: 'Donate Online', Icon: AccountBalanceWalletIcon, gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)', disabled: !donationEnabled },
    { title: d.bankTransferTitle, desc: d.bankTransferDescription, to: '/contact', btn: 'Contact Office', Icon: AccountBalanceIcon, gradient: 'linear-gradient(135deg, #475569, #334155)', disabled: false },
  ];

  const usageItems = [
    'Ceremonies & daily rituals',
    'Community services & annadanam',
    'Structural upkeep & repairs',
    'Priest support & training',
    'Festival activities & cultural programs',
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO
        title="Donate & Support — Kottayil Temple | ക്ഷേത്ര സംഭാവന"
        description="Support Kottayil Temple (Muchukunnu Sri Kotta-Kovilakam Kshethram) through donations. Contribute to temple maintenance, festivals, annadanam, and renovation."
        path="/donate"
        keywords="Kottayil temple donation, Kottayil temple Muchukunnu donation, donate to Kottayil Shiva temple, Muchukunnu temple support, annadanam Kottayil temple, temple contribution Kerala, Kozhikode temple donation, ക്ഷേത്ര സംഭാവന, കൊട്ടയിൽ ക്ഷേത്രം സംഭാവന, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന്, temple donation Kerala"
      />
      <PageBanner title="Donate & Support" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #10b981 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <VolunteerActivismIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Support & Contribute" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      {d.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  {d.mainDescription}
                </Typography>
              </Box>
            </Box>

            {/* ══════ GIVING LEVELS ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #059669, #10b981)',
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                  {d.suggestedLevelsTitle}
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Grid container spacing={2.5}>
                  {levels.map((level, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                      <Box sx={{
                        borderRadius: '20px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', '& .lvlIcon': { transform: 'scale(1.15) rotate(8deg)' } },
                      }}>
                        <Box sx={{ background: level.gradient, px: 2.5, pt: 2.5, pb: 3.5, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
                          <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                          <Box className="lvlIcon" sx={{
                            width: 48, height: 48, borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                            border: '2px solid rgba(255,255,255,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto', mb: 1.5, position: 'relative', zIndex: 2,
                            transition: 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                          }}>
                            <level.Icon sx={{ fontSize: 22, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', position: 'relative', zIndex: 2 }}>{level.title}</Typography>
                          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.3rem' }, color: '#fff', position: 'relative', zIndex: 2, mt: 0.5 }}>{level.amount}</Typography>
                          <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                        </Box>
                        <Box sx={{ px: 2, pt: 0, pb: 2 }}>
                          <Typography sx={{ fontSize: '0.76rem', color: '#888', lineHeight: 1.7, textAlign: 'center' }}>{level.description}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* ══════ DONATION DISABLED WARNING ══════ */}
            {!donationEnabled && (
              <Box sx={{
                mb: 3, borderRadius: '20px', overflow: 'hidden',
                border: '1.5px solid rgba(245,158,11,0.3)',
                bgcolor: '#fffbeb',
                boxShadow: '0 4px 20px rgba(245,158,11,0.08)',
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  px: { xs: 2.5, sm: 3 }, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <BlockIcon sx={{ fontSize: 20, color: '#fff' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>
                    Online Donation Disabled
                  </Typography>
                </Box>
                <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.7, fontWeight: 600 }}>
                    Online donation is currently not available. Please contact the temple office directly for making donations.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 500 }}>
                      Visit the Contact page or call the temple office for assistance.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ══════ DONATION METHODS ══════ */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {methods.map((m, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    opacity: m.disabled ? 0.45 : 1,
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    pointerEvents: m.disabled ? 'none' : 'auto',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 44px rgba(0,0,0,0.07)' },
                  }}>
                    <Box sx={{ background: m.disabled ? 'linear-gradient(135deg, #9ca3af, #6b7280)' : m.gradient, px: 2.5, pt: 2.5, pb: 4, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                        <Box sx={{
                          width: 44, height: 44, borderRadius: '14px',
                          bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                          border: '2px solid rgba(255,255,255,0.25)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <m.Icon sx={{ fontSize: 22, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                          {m.title}{m.disabled ? ' (Disabled)' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                    </Box>
                    <Box sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#888', lineHeight: 1.7, mb: 2 }}>{m.desc}</Typography>
                      {!m.disabled && (
                        <Button component={RouterLink} to={m.to} endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />} sx={{
                          background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', fontWeight: 700,
                          px: 2.5, py: 0.8, borderRadius: '12px', fontSize: '0.78rem', textTransform: 'none',
                          boxShadow: '0 4px 16px rgba(16,185,129,0.25)',
                          '&:hover': { boxShadow: '0 8px 24px rgba(16,185,129,0.35)' },
                        }}>
                          {m.btn}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ USAGE & IMPACT (Dark Slate Footer) ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', mb: 3.5,
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <HistoryEduIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>{d.usageTitle}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, mb: 2.5 }}>{d.usageDescription}</Typography>
                <Grid container spacing={1.5}>
                  {usageItems.map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#10b981', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{item}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* ══════ IMPACT STORIES ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #10b981, #34d399)',
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>{d.impactTitle}</Typography>
              </Box>
              <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5 }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#888', lineHeight: 1.8 }}>{d.impactDescription}</Typography>
                <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {donationEnabled && (
                    <Button component={RouterLink} to="/donate/online" endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />} sx={{
                      background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', fontWeight: 800,
                      px: 3, py: 1.2, borderRadius: '14px', fontSize: '0.85rem', textTransform: 'none',
                      boxShadow: '0 8px 28px rgba(16,185,129,0.3)',
                      transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      '&:hover': { transform: 'scale(1.03)', boxShadow: '0 12px 36px rgba(16,185,129,0.4)' },
                    }}>
                      Donate Now
                    </Button>
                  )}
                  <Button component={RouterLink} to="/donations" sx={{
                    border: '1.5px solid rgba(16,185,129,0.3)', color: '#059669', fontWeight: 700,
                    px: 3, py: 1.1, borderRadius: '14px', fontSize: '0.82rem', textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.4)' },
                  }}>
                    See More Options
                  </Button>
                </Box>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="donate" activePath="/donate" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
